"use client";

import { useState, useMemo, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ArrowUpDown } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { useTenancy } from "@/contexts/TenancyContext";
import { cleanDateAndTimeObject, toLocalDateString } from "@shared/renderDate";
import { Timestamp } from "firebase/firestore";

interface UserPolicyDetailsProps {
  user: any;
}

export function UserPolicyDetails({ user }: UserPolicyDetailsProps) {
  const { activeTenancy } = useTenancy();
  const [activeTab, setActiveTab] = useState("policies");
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>(null);
  const [policyNames, setPolicyNames] = useState<Record<string, string>>({});
  const [policyLastRead, setPolicyLastRead] = useState<Record<string, Date>>({});
  const [isLoading, setIsLoading] = useState(false);

  // Fetch and process reading progress for all policies
  useEffect(() => {
    if (!user?.id || !user?.policyDocs?.length) return;

    const fetchAndProcessReadingProgress = async () => {
      setIsLoading(true);
      const lastReadTimes: Record<string, Date> = {};
      const policyNamesMap: Record<string, string> = {};

      try {
        // Fetch all reading progress data once
        console.log('Fetching reading progress for user:', user.id);
        const progressRef = collection(db, 'users', user.id, 'usersActualReadTime');
        const progressSnap = await getDocs(progressRef);

        // make a { [policyId]: data } map for fast lookup
        const progressById: Record<string, any> =
          Object.fromEntries(progressSnap.docs.map(d => [d.id, d.data()]));
        console.log('Progress data:', progressById);

        // Process each policy
        for (const policy of user.policyDocs) {
          try {
            const policyData = typeof policy.data === 'function' ? policy.data() : policy;
            console.log('Policy data:', policyData);
            const policyId = policyData?.reference;

            // Skip if no policy ID or if it's the template
            if (!policyId || policyId === 'template') continue;

            // Store policy name
            policyNamesMap[policyId] = policyData.name || `Policy ${policyId}`;

            // Hit /policies/{policyId} to get the policy name
            const policyRef = doc(db, 'customers', activeTenancy?.id, 'product', 'policyPackages', 'policies', policyId);
            const policySnap = await getDoc(policyRef);
            if (policySnap.exists()) {
              policyNamesMap[policyId] = policySnap.data().policy_name || `Policy ${policyId}`;
            }

            // Process reading progress for this policy
            const policyProgress = progressById[policyId] || {};
            console.log('Policy progress:', policyProgress, ' for policy:', policyId);
            const lastReadTime = findMostRecentReadTime(policyProgress);
            console.log('Last read time:', lastReadTime, ' for policy:', policyId);

            if (lastReadTime) {
              lastReadTimes[policyId] = lastReadTime;
            }
          } catch (error) {
            console.error(`Error processing policy ${policy.id}:`, error);
          }
        }

        // Update state with the collected data
        setPolicyNames(prev => ({ ...prev, ...policyNamesMap }));
        setPolicyLastRead(prev => ({ ...prev, ...lastReadTimes }));
      } catch (error) {
        console.error('Error fetching reading progress:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAndProcessReadingProgress();
  }, [user?.id, user?.policyDocs]);

  // Function to find the most recent timestamp from reading progress
  const findMostRecentReadTime = (progressData: any): Date | null => {
    if (!progressData) {
      console.log('No progress data found');
      return null;
    }

    let mostRecent: Date | null = null;

    // Process all timestamp fields in the progress data
    Object.values(progressData).forEach((value: any) => {
      if (value && cleanDateAndTimeObject(value)) {
        try {
          const date = new Date(cleanDateAndTimeObject(value));
          if (!isNaN(date.getTime()) && (!mostRecent || date > mostRecent)) {
            mostRecent = date;
          }
        } catch (e) {
          console.error('Error parsing date:', e);
        }
      }
    });

    return mostRecent;
  };

  // Fetch reading progress for a policy
  const fetchReadingProgress = async (policyId: string, userId: string) => {
    if (!userId || !policyId) return null;

    try {
      const progressRef = doc(db, 'usersActualReadingProgress', userId);
      const progressSnap = await getDoc(progressRef);

      if (progressSnap.exists()) {
        const progressData = progressSnap.data();
        // Find the specific policy's progress
        const policyProgress = progressData[policyId] || {};
        return findMostRecentReadTime(policyProgress);
      }
      return null;
    } catch (error) {
      console.error(`Error fetching reading progress for policy ${policyId}:`, error);
      return null;
    }
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp: any) => {
    try {
      if (!timestamp) return 'N/A';
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleString();
    } catch (error) {
      console.error('Error formatting timestamp:', error);
      return 'Invalid date';
    }
  };

  // Get policy status badge
  const getPolicyStatusBadge = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'true':
        return <Badge variant="success">Completed</Badge>;
      case 'false':
        return <Badge variant="warning">In Progress</Badge>;
      default:
        return <Badge variant="outline">Not Started</Badge>;
    }
  };

  return (
    <div className="space-y-4">
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="policies">Policies</TabsTrigger>
          <TabsTrigger value="quizzes">Quizzes</TabsTrigger>
        </TabsList>

        <TabsContent value="policies" className="mt-0">
          <ScrollArea className="h-[300px] rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Policy Name</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Assigned On</TableHead>
                  <TableHead>Last Read</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {user.policyDocs && user.policyDocs.length > 0 ? (
                  user.policyDocs.map((policy: any, index: number) => {
                    const policyData = typeof policy.data === 'function' ? policy.data() : policy;
                    return (
                      <TableRow key={index}>
                        <TableCell className="font-medium">
                          {policyNames[policyData.reference] || `Policy ${index + 1}`}
                        </TableCell>
                        <TableCell>
                          {getPolicyStatusBadge(policyData.completed)}
                        </TableCell>
                        <TableCell>
                          {formatTimestamp(policyData.dateAssigned)}
                        </TableCell>
                        <TableCell>
                          {policyLastRead[policyData.reference] ? (
                            formatTimestamp(policyLastRead[policyData.reference])
                          ) : (
                            <span className="text-muted-foreground">Never read</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={4} className="h-24 text-center">
                      No policies assigned
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </TabsContent>

        <TabsContent value="quizzes" className="mt-0">
          <ScrollArea className="h-[300px] rounded-md border">
            <div className="p-4">
              <div className="mb-6">
                <h3 className="text-lg font-medium mb-2">Quiz Completion Overview</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-card rounded-lg p-4 border">
                    <h4 className="text-sm font-medium text-muted-foreground mb-2">Overall Quiz Performance</h4>
                    <div className="flex items-end justify-between">
                      <div>
                        <p className="text-2xl font-bold">{user.quizPercentage}%</p>
                        <p className="text-sm text-muted-foreground">Success Rate</p>
                      </div>
                      <Progress
                        value={parseInt(user.quizPercentage)}
                        max={100}
                        className="h-2 w-1/2"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="text-sm font-medium">Quiz Results</h3>
                {user.quizDetails && user.quizDetails.length > 0 ? (
                  <div className="border rounded-md">
                    <Table>
                      <TableHeader>
                        <TableRow className="h-10">
                          <TableHead className="w-[200px]">Policy</TableHead>
                          <TableHead className="w-[100px] text-center">Status</TableHead>
                          <TableHead className="w-[150px]">Progress</TableHead>
                          <TableHead className="w-[100px] text-center">Score</TableHead>
                          <TableHead className="w-[100px] text-center">Attempts</TableHead>
                          <TableHead className="w-[150px]">Last Attempt</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {user.quizDetails.map((detail: any, index: number) => {
                          const { policyId, bestAttempt, lastAttempt, totalAttempts = 0 } = detail;
                          const policyName = policyNames[policyId] || `Policy ${policyId}`;
                          const score = bestAttempt?.score || 0;
                          const numericPercentage = Math.round(score);
                          const hasPassed = bestAttempt?.passed || false;

                          return (
                            <TableRow key={index} className="h-12">
                              <TableCell className="font-medium">
                                <div className="flex flex-col">
                                  <span className="text-sm">{policyName}</span>
                                  <span className="text-xs text-muted-foreground">ID: {policyId}</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-center">
                                <Badge
                                  variant={totalAttempts > 0 ? (hasPassed ? "success" : "destructive") : "outline"}
                                  className="text-xs px-2 py-0.5"
                                >
                                  {totalAttempts > 0 ? (hasPassed ? 'Passed' : 'Failed') : 'Not Started'}
                                </Badge>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <div className="h-2 w-16 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-800">
                                    <div
                                      className={`h-full ${numericPercentage >= 70 ? 'bg-green-500' : 'bg-primary'
                                        }`}
                                      style={{ width: `${numericPercentage}%` }}
                                    />
                                  </div>
                                  <span className="text-xs text-muted-foreground">{numericPercentage}%</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-center">
                                {totalAttempts > 0 ? (
                                  <span className={`text-sm font-medium ${hasPassed ? 'text-green-600 dark:text-green-400' : 'text-destructive'}`}>
                                    {numericPercentage}%
                                  </span>
                                ) : (
                                  <span className="text-xs text-muted-foreground">-</span>
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                <span className="text-sm">{totalAttempts}</span>
                              </TableCell>
                              <TableCell>
                                {lastAttempt?.timestamp ? (
                                  <div className="text-xs text-muted-foreground">
                                    {formatTimestamp(lastAttempt.timestamp)}
                                  </div>
                                ) : (
                                  <span className="text-xs text-muted-foreground">-</span>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  <div className="text-center py-6 text-sm text-muted-foreground">
                    No quiz results available
                  </div>
                )}
              </div>
            </div>
          </ScrollArea>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Helper function to check quiz answers
function checkAnswers(data: any) {
  let correctCount = 0;
  let totalQuestions = 0;

  // Check each question and answer
  for (const key in data) {
    if (Object.prototype.hasOwnProperty.call(data, key)) {
      // Skip non-question fields
      if (!key.endsWith('CorrectAnswer')) continue;

      totalQuestions++;
      const questionNumber = key.replace('CorrectAnswer', '');
      const userAnswerKey = `${questionNumber}UserAnswer`;

      if (data[userAnswerKey] === data[key]) {
        correctCount++;
      }
    }
  }

  return {
    correctCount,
    totalQuestions,
    percentage: totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0,
    allCorrect: correctCount === totalQuestions && totalQuestions > 0
  };
}
